import { canOpenResource } from '../../access';
import { policyAllows } from '../../modules';
import type { Entity, Snapshot } from '../../types';
/** Execution-only projection: published values, no endpoints, credentials or drafts. */
export function publishedExecutionSettings(
  state: Snapshot,
  kind: Entity['kind'],
): Entity[] {
  const keys = (
    {
      rule: ['keyword', 'exclusion'],
      modelConfig: ['parallel', 'timeoutMs'],
      subscription: ['unitPrice', 'expiresAt', 'analysisLimit'],
      template: ['analysisTemplate'],
    } as Record<string, string[]>
  )[kind];
  if (
    !keys ||
    !['analysis', 'analysisTemplate', 'evaluation'].some((r) =>
      canOpenResource(r, state),
    ) ||
    !policyAllows(state, kind, 'read')
  )
    return [];
  return state.entities
    .filter(
      (e) =>
        e.kind === kind &&
        e.status === 'published' &&
        e.fields?.softDeleted !== 'true',
    )
    .map((e) => ({
      uuid: e.uuid,
      kind: e.kind,
      title: e.title,
      revision: e.revision,
      status: e.status,
      createdAt: e.createdAt,
      updatedAt: e.updatedAt,
      campus: e.campus,
      memberUuid: '',
      note: '',
      fields: projectFields(e, keys),
    }));
}

function projectFields(row: Entity, keys: string[]): Record<string, string> {
  if (row.kind === 'template') {
    try {
      const data = JSON.parse(row.fields?.analysisTemplate || '{}');
      const published = data.versions?.find(
        (v: { uuid: string }) => v.uuid === data.publishedVersionUuid,
      );
      return published
        ? {
            analysisTemplate: JSON.stringify({
              draft: published.settings,
              versions: data.versions,
              publishedVersionUuid: published.uuid,
            }),
          }
        : {};
    } catch {
      return {};
    }
  }
  return Object.fromEntries(
    keys
      .filter((k) => row.fields?.[k] !== undefined)
      .map((k) => [k, row.fields?.[k] || '']),
  );
}
export function isExecutionProjection(namespace: string, kind: Entity['kind']) {
  return (
    namespace.startsWith('analysis') &&
    (['rule', 'modelConfig', 'subscription'].includes(kind) ||
      (kind === 'template' && namespace !== 'analysis-template'))
  );
}
